import { useEffect, useRef, type JSX } from 'react';
import { createPortal } from 'react-dom';
import { Button, IconChevronRight, IconDownload, Modal } from '@taki/ui';
import { imageVoisine, rangDImage } from './vignettes.js';

/* Un geste de la visionneuse : un carre, le glyphe seul, comme la croix du `Modal`. */
const GESTE = 'h-9 w-9 shrink-0 px-0';

/** Ce que la visionneuse sait d'une image : son id et son nom. */
export interface ImageDeVisionneuse {
  id: string;
  name: string;
}

/**
 * LA VISIONNEUSE DES IMAGES DE LA CARTE DES FICHIERS : une fenetre qui parcourt
 * les images de la liste chargee, bornee (pas de retour a l'autre bout : le
 * bouton se grise).
 *
 * LA FENETRE EST LE `Modal` DU SOCLE, a sa taille `plein` : le voile floute de
 * « Lier une fiche », SANS PANNEAU (recette du 02/11 : le cadre blanc et le nom
 * en grand mangeaient la place, l'image est le seul sujet). Il apporte aussi le
 * dialogue (`useDialogFocus` : clavier garde dans la fenetre, Echap, isolement
 * de l'ecran de dessous, focus rendu a l'element qui l'a ouverte) et la croix.
 * Le NOM du fichier est son titre, lu par les lecteurs d'ecran seulement, la
 * POSITION (« 2 / 5 ») son sous-titre, en petit en haut a gauche, et
 * « Télécharger », une icone, son pied, a gauche de la croix. Les fleches
 * gauche et droite s'ecoutent sur le document : la visionneuse garde ses
 * fleches meme quand le focus est perdu (clic dans l'image).
 *
 * Les octets sont ceux que la carte a deja lus pour ses vignettes (aucune URL
 * durable) : `urlDe` rend l'URL d'objet, ou `undefined` tant qu'elle se lit.
 */
export function VisionneuseDImages({ images, courante, urlDe, illisible, occupe, message, onNaviguer, onTelecharger, onFermer }: {
  images: readonly ImageDeVisionneuse[];
  /** L'id de l'image montree. */
  courante: string;
  urlDe: (id: string) => string | undefined;
  illisible: (id: string) => boolean;
  /** Une lecture est en cours (le telechargement grise son bouton). */
  occupe: boolean;
  /** Une erreur de la carte, dite ici puisque la carte est cachee derriere. */
  message: string | null;
  onNaviguer: (id: string) => void;
  onTelecharger: (id: string) => void;
  onFermer: () => void;
}): JSX.Element | null {
  const rang = rangDImage(images, courante);
  const image = images[rang];
  const precedente = imageVoisine(images, courante, -1);
  const suivante = imageVoisine(images, courante, 1);

  const naviguer = useRef(onNaviguer);
  naviguer.current = onNaviguer;
  useEffect(() => {
    const auClavier = (event: KeyboardEvent): void => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const cible = event.key === 'ArrowLeft' ? precedente : event.key === 'ArrowRight' ? suivante : null;
      if (cible === null) return;
      event.preventDefault();
      naviguer.current(cible);
    };
    document.addEventListener('keydown', auClavier);
    return () => document.removeEventListener('keydown', auClavier);
  }, [precedente, suivante]);

  if (image === undefined) return null;
  const url = urlDe(image.id);

  /* Dans un portail sur le body, comme « Lier une fiche » : un ancetre
     transforme de la fiche deviendrait le repere de tout ce qui est `fixed`. */
  return createPortal(
    <Modal
      open
      onClose={onFermer}
      title={image.name}
      description={`${rang + 1} / ${images.length}`}
      size="plein"
      footer={<Button variant="secondary" size="sm" className={GESTE} disabled={occupe} onClick={() => onTelecharger(image.id)} title="Télécharger" aria-label={`Télécharger ${image.name}`}><IconDownload size={16} /></Button>}
    >
      {/* L'IMAGE PREND TOUT L'ECRAN, a meme le voile : les fleches l'encadrent a
          mi-hauteur, et le bas garde la hauteur de la barre du haut, pour que
          l'image reste centree. Seuls l'image et les gestes prennent les clics :
          a cote, le clic tombe sur le voile et ferme. */}
      <div className="flex h-full min-h-0 flex-col">
        {message !== null && <p role="alert" className="pointer-events-auto shrink-0 text-center text-sm text-danger">{message}</p>}
        <div className="flex min-h-0 flex-1 items-center gap-4 pb-9 pt-3">
          <Button variant="secondary" size="sm" className={`pointer-events-auto ${GESTE}`} disabled={precedente === null} onClick={() => { if (precedente !== null) onNaviguer(precedente); }} title="Précédent" aria-label="Précédent"><IconChevronRight size={18} className="rotate-180" /></Button>
          <div className="flex h-full min-w-0 flex-1 items-center justify-center">
            {url !== undefined
              ? <img src={url} alt={image.name} className="pointer-events-auto max-h-full max-w-full rounded-lg object-contain shadow-panel" />
              : <p role="status" className="text-sm text-ink-500">{illisible(image.id) ? 'Image illisible.' : 'Chargement de l\'image...'}</p>}
          </div>
          <Button variant="secondary" size="sm" className={`pointer-events-auto ${GESTE}`} disabled={suivante === null} onClick={() => { if (suivante !== null) onNaviguer(suivante); }} title="Suivant" aria-label="Suivant"><IconChevronRight size={18} /></Button>
        </div>
      </div>
    </Modal>,
    document.body,
  );
}
